import { WizardAbstractService } from "../service/wizards/wizard-abstract.service";

/**
 * Modèle représentant une étape du wizard.
 */
export class WizardStep {
    id: string;
    text: string;
    title?: string;
    attachTo?: {
        element: string;
        on: string;
    };
    advanceOn?: {
        selector: string;
        event: string;
    };
    buttons?: Array<{
        text: string;
        action: () => void;
        classes?: string;
    }>;
    beforeShowPromise?: () => Promise<void | boolean>;
    disableInteraction?: boolean;
    when?: { show?: () => void; hide?: () => void; };


    constructor() { }

    /**
       * Crée un nouveau builder pour une étape du wizard
       * @param id Identifiant unique de l'étape
       * @param wizardService Service du wizard (requis pour utiliser les helpers de navigation)
       */
    static create(id: string, wizardService: WizardAbstractService): WizardStepBuilder {
        return new WizardStepBuilder(id, wizardService);
    }

    /**
     * Convertit le step en format Shepherd
     */
    toShepherdStep(): any {
        return {
            id: this.id,
            title: this.title,
            text: this.text,
            attachTo: this.attachTo,
            advanceOn: this.advanceOn,
            buttons: this.buttons,
            beforeShowPromise: this.beforeShowPromise,
            when: this.when
        };
    }
}

/**
 * Builder pour construire des étapes de wizard de manière fluide
 */
export class WizardStepBuilder {
    private step: WizardStep;
    private wizardService: WizardAbstractService;

    constructor(id: string, wizardService: WizardAbstractService) {
        this.step = new WizardStep();
        this.step.id = id;
        this.wizardService = wizardService;
    }

    /**
    * Définit le titre de l'étape (affiché dans le header)
    */
    withTitle(title: string): this {
        this.step.title = title;
        return this;
    }

    /**
     * Définit le texte de l'étape
     */
    withText(text: string): this {
        this.step.text = text;
        return this;
    }

    /**
     * Attache l'étape à un élément du DOM
     */
    attachTo(element: string, position: 'top' | 'bottom' | 'left' | 'right' = 'bottom'): this {
        this.step.attachTo = { element, on: position };
        return this;
    }

    /**
     * Configure l'avancement automatique sur un événement
     */
    advanceOn(selector: string, event: string = 'click'): this {
        this.step.advanceOn = { selector, event };
        return this;
    }

    /**
     * Ajoute un bouton
     */
    addButton(text: string, action: () => void, classes?: string): this {
        if (!this.step.buttons) {
            this.step.buttons = [];
        }
        this.step.buttons.push({ text, action, classes });
        return this;
    }

    /**
     * Ajoute un bouton "Suivant"
     */
    addNextButton(text: string = 'Suivant', classes?: string): this {
        return this.addButton(text, () => this.wizardService.next(), classes);
    }

    /**
     * Ajoute un bouton "Précédent"
     */
    addBackButton(text: string = 'Précédent', classes: string = 'shepherd-button-secondary'): this {
        return this.addButton(text, () => this.wizardService.back(), classes);
    }

    /**
     * Ajoute un bouton "Terminer"
     */
    addCompleteButton(text: string = 'Terminer', classes?: string): this {
        return this.addButton(text, () => this.wizardService.complete(), classes);
    }

    /**
     * Ajoute un bouton "Annuler"
     */
    addCancelButton(text: string = 'Annuler', classes: string = 'shepherd-button-cancel'): this {
        return this.addButton(text, () => this.wizardService.cancel(), classes);
    }

    /**
     * Attend automatiquement que l'élément attaché soit présent (comportement par défaut)
     */
    waitForElement(timeout: number = 5000): this {
        const element = this.step.attachTo?.element;
        if (!element) {
            console.warn('Cannot wait for element: no attachTo defined');
            return this;
        }

        this.step.beforeShowPromise = () => {
            return this.waitForElementHelper(element, timeout);
        };
        return this;
    }

    /**
     * Attend qu'un select soit prêt avec des options
     */
    waitForSelectWithOptions(timeout: number = 3000): this {
        const element = this.step.attachTo?.element;
        if (!element) {
            console.warn('Cannot wait for select: no attachTo defined');
            return this;
        }

        this.step.beforeShowPromise = () => {
            return this.waitForSelectWithOptionsHelper(element, timeout);
        };
        return this;
    }

    /**
     * Définit une beforeShowPromise personnalisée (override)
     */
    withCustomBeforeShow(promiseFn: () => Promise<void | boolean>): this {
        this.step.beforeShowPromise = promiseFn;
        return this;
    }

    /**
     * Désactive les interactions avec l'élément highlighted pendant cette étape
     */
    disableTargetInteraction(): this {
        if (this.step.attachTo) {
            this.step.when = {
                show: async () => {
                    const element = await this.waitForElementToExist(this.step.attachTo!.element);
                    if (element) {
                        element.dataset.wizardOriginalPointerEvents = element.style.pointerEvents || '';
                        element.dataset.wizardOriginalCursor = element.style.cursor || '';
                        element.style.pointerEvents = 'none';
                        element.style.cursor = 'not-allowed';
                        console.log('Element disabled:', this.step.attachTo!.element);
                    }
                },
                hide: async () => {
                    const element = await this.waitForElementToExist(this.step.attachTo!.element, 500);
                    if (element) {
                        element.style.pointerEvents = element.dataset.wizardOriginalPointerEvents || '';
                        element.style.cursor = element.dataset.wizardOriginalCursor || '';
                        delete element.dataset.wizardOriginalPointerEvents;
                        delete element.dataset.wizardOriginalCursor;
                        console.log('Element enabled:', this.step.attachTo!.element);
                    }
                }
            };
        }

        return this;
    }

    /**
     * Construit l'étape finale
     */
    build(): WizardStep {
        if (!this.step.text) {
            throw new Error('Step text is required');
        }
        return this.step;
    }

    // ========== HELPERS ==========

    private waitForElementHelper(selector: string, timeout: number): Promise<void> {
        return new Promise((resolve) => {
            const startTime = Date.now();

            const checkElement = () => {
                const element = document.querySelector(selector);

                if (element) {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    console.warn(`Element ${selector} not found after ${timeout}ms`);
                    resolve();
                } else {
                    setTimeout(checkElement, 100);
                }
            };

            checkElement();
        });
    }

    /**
     * Helper pour disableTargetInteraction() - retourne HTMLElement | null
     */
    private waitForElementToExist(selector: string, timeout: number = 2000): Promise<HTMLElement | null> {
        return new Promise((resolve) => {
            const startTime = Date.now();

            const checkElement = () => {
                const element = document.querySelector(selector) as HTMLElement;

                if (element) {
                    resolve(element);
                } else if (Date.now() - startTime > timeout) {
                    console.warn(`Element ${selector} not found after ${timeout}ms`);
                    resolve(null);
                } else {
                    setTimeout(checkElement, 100);
                }
            };

            checkElement();
        });
    }

    private waitForSelectWithOptionsHelper(selector: string, timeout: number): Promise<boolean> {
        return new Promise((resolve) => {
            const startTime = Date.now();

            const checkElement = () => {
                const select = document.querySelector(selector) as HTMLSelectElement;

                if (select && !select.disabled && select.options.length > 1) {
                    resolve(true);
                } else if (Date.now() - startTime > timeout) {
                    console.warn(`Select ${selector} not ready after ${timeout}ms`);
                    resolve(false);
                } else {
                    setTimeout(checkElement, 200);
                }
            };

            checkElement();
        });
    }
}